import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, CheckCircle2, AlertCircle, AlertTriangle, Copy, FileText } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { type Classroom } from '../../../accounts';
interface BulkImportProps {
  teacherId: string;
  onComplete: () => void;
  onCancel: () => void;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
}
interface ParsedStudent {
  name: string;
  grade: number;
  status: 'pending' | 'success' | 'error';
  accountId?: string;
  linkCode?: string;
  errorMsg?: string;
  warnings: string[];
}
export function BulkImport({
  teacherId,
  onComplete,
  onCancel,
  classrooms = [],
  selectedClassroomId = null
}: BulkImportProps) {
  const {
    createAndLinkStudent,
    accounts
  } = useAppStore();
  const defaultClassroom = selectedClassroomId ? classrooms.find(c => c.id === selectedClassroomId) : null;
  const [inputText, setInputText] = useState('');
  const [defaultGrade, setDefaultGrade] = useState(defaultClassroom ? defaultClassroom.groupLevel.toString() : '6');
  const [targetClassroomId, setTargetClassroomId] = useState<string>(selectedClassroomId || '');
  const [parsedStudents, setParsedStudents] = useState<ParsedStudent[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState<'input' | 'preview' | 'result'>('input');
  const [copied, setCopied] = useState(false);
  /** Get existing learner names in the target classroom for duplicate detection */
  const getExistingLearnerNames = (): string[] => {
    const targetClassroom = classrooms.find(c => c.id === targetClassroomId);
    if (!targetClassroom || !accounts?.accountsById) return [];
    return targetClassroom.learnerIds.map(id => {
      const account = accounts.accountsById[id];
      return account?.displayName?.toLowerCase().trim() || '';
    }).filter(Boolean);
  };
  const handleParse = () => {
    if (!inputText.trim()) return;
    const lines = inputText.split('\n').filter(line => line.trim().length > 0);
    const targetClassroom = classrooms.find(c => c.id === targetClassroomId);
    const existingNames = getExistingLearnerNames();
    const seenNames = new Set<string>();
    const students: ParsedStudent[] = lines.map(line => {
      const parts = line.split(',').map(p => p.trim());
      const name = parts[0];
      let grade = parseInt(defaultGrade, 10);
      if (parts.length > 1) {
        const parsedGrade = parseInt(parts[1], 10);
        if (!isNaN(parsedGrade) && parsedGrade >= 1 && parsedGrade <= 8) {
          grade = parsedGrade;
        }
      }
      const warnings: string[] = [];
      // Validate: empty name
      if (!name || name.trim().length === 0) {
        warnings.push('Naam is leeg');
      }
      // Validate: grade mismatch with target classroom
      if (targetClassroom && grade !== targetClassroom.groupLevel) {
        warnings.push(`Groep ${grade} komt niet overeen met klas "${targetClassroom.name}" (groep ${targetClassroom.groupLevel})`);
      }
      // Validate: duplicate with existing learners in classroom
      const normalizedName = name?.toLowerCase().trim() || '';
      if (normalizedName && existingNames.includes(normalizedName)) {
        warnings.push(`"${name}" bestaat al in deze klas`);
      }
      // Validate: duplicate within import list
      if (normalizedName && seenNames.has(normalizedName)) {
        warnings.push(`"${name}" komt meerdere keren voor in de lijst`);
      }
      seenNames.add(normalizedName);
      return {
        name,
        grade,
        status: 'pending',
        warnings
      };
    });
    setParsedStudents(students);
    setStep('preview');
  };
  const handleImport = async () => {
    setIsProcessing(true);
    const results = [...parsedStudents];
    for (let i = 0; i < results.length; i++) {
      const student = results[i];
      try {
        // F4: Atomic operation — create account, link to teacher, generate code in one state update
        const {
          studentId,
          linkCode
        } = createAndLinkStudent(teacherId, {
          displayName: student.name,
          grade: student.grade
        }, targetClassroomId || undefined);
        results[i] = {
          ...student,
          status: 'success',
          accountId: studentId,
          linkCode
        };
      } catch (err) {
        results[i] = {
          ...student,
          status: 'error',
          errorMsg: 'Fout bij aanmaken'
        };
      }
    }
    setParsedStudents(results);
    setIsProcessing(false);
    setStep('result');
  };
  const copyAllCodes = () => {
    const text = parsedStudents.filter(s => s.status === 'success' && s.linkCode).map(s => `${s.name}: ${s.linkCode}`).join('\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const totalWarnings = parsedStudents.reduce((sum, s) => sum + s.warnings.length, 0);
  return <div className="space-y-4" data-id="element-4721">
      {step === 'input' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="space-y-4" data-id="element-4722">
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 text-sm text-slate-600" data-id="element-4723">
            <p className="font-semibold text-slate-800 mb-1" data-id="element-4724">Hoe werkt het?</p>
            <p data-id="element-4725">
              Plak een lijst met namen (één per regel). Je kunt optioneel de
              groep toevoegen met een komma (bijv. "Emma de Vries, 7").
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4726">
            <Select variant="admin" label="Standaard groep (als niet opgegeven)" value={defaultGrade} onChange={e => setDefaultGrade(e.target.value)} options={[{
          value: '6',
          label: 'Groep 6'
        }, {
          value: '7',
          label: 'Groep 7'
        }, {
          value: '8',
          label: 'Groep 8'
        }]} data-id="element-4727" />

            {classrooms.length > 0 && <Select variant="admin" label="Toevoegen aan klas" value={targetClassroomId} onChange={e => setTargetClassroomId(e.target.value)} placeholder="Geen klas" options={classrooms.map(c => ({
          value: c.id,
          label: c.name
        }))} data-id="element-4728" />}
          </div>

          <div data-id="element-4729">
            <label className="block text-sm font-medium text-slate-700 mb-1 flex justify-between" data-id="element-4730">
              <span data-id="element-4731">Namenlijst</span>
              <span className="text-slate-400 font-normal" data-id="element-4732">
                Eén leerling per regel
              </span>
            </label>
            <textarea value={inputText} onChange={e => setInputText(e.target.value)} rows={8} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm" placeholder={'Emma de Vries\nNoah Jansen, 7\nSophie Bakker'} data-id="element-4733" />
          </div>

          <div className="pt-2 flex justify-end gap-2" data-id="element-4734">
            <Button variant="secondary" onClick={onCancel} data-id="element-4735">
              Annuleren
            </Button>
            <Button onClick={handleParse} disabled={!inputText.trim()} data-id="element-4736">
              <FileText className="w-4 h-4 mr-2" data-id="element-4737" />
              Namen controleren
            </Button>
          </div>
        </motion.div>}

      {step === 'preview' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="space-y-4" data-id="element-4738">
          <div className="flex items-center justify-between mb-2" data-id="element-4739">
            <h4 className="font-bold text-slate-800" data-id="element-4740">
              Controleer de lijst ({parsedStudents.length} leerlingen)
            </h4>
            <button onClick={() => setStep('input')} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium" data-id="element-4741">
              Lijst aanpassen
            </button>
          </div>

          {/* Validation warnings banner */}
          {totalWarnings > 0 && <div className="bg-amber-50 p-3 rounded-xl border border-amber-200 flex items-start gap-3" data-id="element-4742">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-4743" />
              <div data-id="element-4744">
                <h4 className="font-bold text-amber-900 text-sm" data-id="element-4745">
                  {totalWarnings} waarschuwing{totalWarnings !== 1 ? 'en' : ''}{' '}
                  gevonden
                </h4>
                <p className="text-xs text-amber-700 mt-0.5" data-id="element-4746">
                  Je kunt toch importeren, maar controleer de gemarkeerde rijen.
                </p>
              </div>
            </div>}

          <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg" data-id="element-4747">
            <table className="w-full text-sm text-left" data-id="element-4748">
              <thead className="bg-slate-50 text-slate-600 sticky top-0" data-id="element-4749">
                <tr data-id="element-4750">
                  <th className="px-4 py-2 font-medium" data-id="element-4751">Naam</th>
                  <th className="px-4 py-2 font-medium w-24" data-id="element-4752">Groep</th>
                  <th className="px-4 py-2 font-medium w-10" data-id="element-4753"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-4754">
                {parsedStudents.map((student, idx) => <tr key={idx} className={`hover:bg-slate-50 ${student.warnings.length > 0 ? 'bg-amber-50/50' : ''}`} data-id="element-4755">
                    <td className="px-4 py-2 font-medium text-slate-800" data-id="element-4756">
                      {student.name}
                    </td>
                    <td className={`px-4 py-2 ${student.warnings.some(w => w.includes('Groep')) ? 'text-amber-700 font-bold' : 'text-slate-600'}`} data-id="element-4757">
                      Groep {student.grade}
                    </td>
                    <td className="px-4 py-2" data-id="element-4758">
                      {student.warnings.length > 0 && <span title={student.warnings.join('\n')} className="cursor-help" data-id="element-4759">
                          <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-4760" />
                        </span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Per-student warning details */}
          {totalWarnings > 0 && <div className="space-y-1" data-id="element-4761">
              {parsedStudents.filter(s => s.warnings.length > 0).map((s, idx) => <div key={idx} className="text-xs text-amber-700 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-100" data-id="element-4762">
                    <span className="font-bold" data-id="element-4763">{s.name || '(leeg)'}:</span>{' '}
                    {s.warnings.join('; ')}
                  </div>)}
            </div>}

          <div className="pt-2 flex justify-end gap-2" data-id="element-4764">
            <Button variant="secondary" onClick={onCancel} disabled={isProcessing} data-id="element-4765">
              Annuleren
            </Button>
            <Button onClick={handleImport} disabled={isProcessing} data-id="element-4766">
              {isProcessing ? <>Bezig met importeren...</> : <>
                  <Upload className="w-4 h-4 mr-2" data-id="element-4767" />
                  Importeer {parsedStudents.length} leerlingen
                </>}
            </Button>
          </div>
        </motion.div>}

      {step === 'result' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="space-y-4" data-id="element-4768">
          <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200 flex items-start gap-3" data-id="element-4769">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-4770" />
            <div data-id="element-4771">
              <h4 className="font-bold text-emerald-900" data-id="element-4772">Import voltooid!</h4>
              <p className="text-sm text-emerald-700" data-id="element-4773">
                Er zijn{' '}
                {parsedStudents.filter(s => s.status === 'success').length}{' '}
                leerlingen toegevoegd aan je klas. Deel onderstaande koppelcodes
                met de leerlingen.
              </p>
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg" data-id="element-4774">
            <table className="w-full text-sm text-left" data-id="element-4775">
              <thead className="bg-slate-50 text-slate-600 sticky top-0" data-id="element-4776">
                <tr data-id="element-4777">
                  <th className="px-4 py-2 font-medium" data-id="element-4778">Naam</th>
                  <th className="px-4 py-2 font-medium w-32" data-id="element-4779">Koppelcode</th>
                  <th className="px-4 py-2 font-medium w-24" data-id="element-4780">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-4781">
                {parsedStudents.map((student, idx) => <tr key={idx} className="hover:bg-slate-50" data-id="element-4782">
                    <td className="px-4 py-2 font-medium text-slate-800" data-id="element-4783">
                      {student.name}
                    </td>
                    <td className="px-4 py-2" data-id="element-4784">
                      {student.linkCode ? <span className="font-mono font-bold text-indigo-700 tracking-wider bg-indigo-50 px-2 py-1 rounded" data-id="element-4785">
                          {student.linkCode}
                        </span> : '-'}
                    </td>
                    <td className="px-4 py-2" data-id="element-4786">
                      {student.status === 'success' ? <span className="text-emerald-600 flex items-center gap-1 text-xs font-medium" data-id="element-4787">
                          <CheckCircle2 className="w-3 h-3" data-id="element-4788" /> Gelukt
                        </span> : <span className="text-red-600 flex items-center gap-1 text-xs font-medium" title={student.errorMsg} data-id="element-4789">
                          <AlertCircle className="w-3 h-3" data-id="element-4790" /> Fout
                        </span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          <div className="pt-2 flex justify-between items-center" data-id="element-4791">
            <Button variant="secondary" onClick={copyAllCodes} className="bg-white" data-id="element-4792">
              {copied ? <>
                  <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500" data-id="element-4793" />{' '}
                  Gekopieerd!
                </> : <>
                  <Copy className="w-4 h-4 mr-2" data-id="element-4794" /> Kopieer alle codes
                </>}
            </Button>
            <Button onClick={onComplete} data-id="element-4795">Klaar</Button>
          </div>
        </motion.div>}
    </div>;
}